import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import {
  assertNever,
  cn,
  getSCMCategory,
  groupBy,
  SCMCategory,
} from "@theprelude/core";
import {
  Button,
  Checkbox,
  DateInput,
  DropdownFormField,
  DurationInput,
  IconButton,
  Input,
  InputField,
  PlusIcon,
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollBar,
  Select,
  SelectOption,
  TrashcanIcon,
} from "@theprelude/ds";
import { Fragment, forwardRef, useImperativeHandle } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { isDeepEqual } from "remeda";
import { z } from "zod";
import {
  Filter,
  filterSchema,
  OdataOperator,
  OdataQuantifier,
  odataOperatorSchema,
} from "@/lib/filter";
import { isValidPythonRegex } from "@/lib/is-valid-python-regex";
import { isDurationFilter } from "@/lib/odata";
import { DropdownSelect } from "../dropdown-select";
import {
  AvailableFilter,
  FilterOption,
  getNamedInstance,
  isDurationId,
  prefixDurationId,
  stripDurationId,
} from "./available-filters";

export type FormFilter = z.infer<typeof formFilterSchema>;
export type FilterFormData = z.infer<typeof filterFormSchema>;

export const formFilterValueSchema = z
  .object({
    operator: odataOperatorSchema,
    value: z.union([z.string(), z.null()]),
  })
  .refine(
    (input) =>
      input.value === null ||
      (input.operator !== "is" && input.operator !== "is not") ||
      input.value !== "",
    {
      message: "String must contain at least 1 character(s)",
      path: ["value"],
    },
  );

export const formFilterSchema = filterSchema
  .extend({
    value: z.union([z.string(), z.null()]),
    operator: z.union([odataOperatorSchema, z.literal("")]),
    ors: z.array(formFilterValueSchema).optional(),
  })
  .refine(
    (input) =>
      input.operator === "" ||
      input.value === null ||
      (input.operator !== "is" && input.operator !== "is not") ||
      input.value !== "",
    {
      message: "String must contain at least 1 character(s)",
      path: ["value"],
    },
  )
  .refine(
    (input) =>
      (input.operator !== "matches" && input.operator !== "does not match") ||
      input.value === null ||
      isValidPythonRegex(input.value),
    { message: "Invalid regular expression", path: ["value"] },
  )
  .refine(
    (input) =>
      !isDurationId(input.id) ||
      input.value === null ||
      isValidDuration(input.value),
    {
      message: "Duration must be at least 1 day",
      path: ["value"],
    },
  );

const filterFormSchema = z.object({
  filters: z.array(formFilterSchema),
});

const DEFAULT_FILTERS: FormFilter[] = [
  { id: "", operator: "", value: "", ors: [] },
];

type FilterFieldValues = FieldValues & { filters: FormFilter[] };

const isValidDuration = (duration?: string) => {
  if (!duration) {
    return true;
  }
  return Boolean(Number.parseInt(duration, 10));
};

export const getDefaultCast = (
  type: AvailableFilter["type"] | undefined = "text",
) => {
  const cast = (value: string) => {
    switch (type) {
      case "boolean": {
        return value === "true";
      }
      case "duration":
      case "number": {
        return Number(value);
      }
      case "text":
      case "date": {
        return String(value);
      }
      default: {
        return assertNever(type);
      }
    }
  };

  return ({ operator, value, ors, ...filter }: FormFilter): Filter => {
    if (operator === "") {
      throw new Error('Invalid operator: ""');
    }
    const finalValue = value === null ? null : cast(value);
    return {
      operator,
      value: finalValue,
      ors: ors?.map((or) => ({
        ...or,
        value: or.value === null ? null : cast(or.value as string),
      })),
      ...filter,
    };
  };
};

export const DEFAULT_PARSE = (filter: Filter): FormFilter => {
  const formFilter = {
    ...filter,
    value: filter.value === null ? null : (filter.value?.toString() ?? ""),
    ors: filter.ors?.map((or) => ({
      operator: or.operator,
      value: or.value === null ? null : (or.value?.toString() ?? ""),
    })),
  } as FormFilter;

  return formFilter;
};

function findMatchingFilter<F extends Filter, AF extends AvailableFilter>(
  filter: F,
  availableFilters: AF[],
  compare?: (filter: F, additionalFilter: AF) => boolean,
): AvailableFilter | undefined {
  let maxMatchLength = -1;
  let matchingFilter: AvailableFilter | undefined = undefined;

  const effectiveFilterId = isDurationFilter(filter)
    ? prefixDurationId(filter.id)
    : filter.id;

  for (const currentFilter of availableFilters) {
    // Check if this could be a duration filter match
    const durationFilterId = prefixDurationId(filter.id);
    const containsId =
      currentFilter.id.split(":").includes(effectiveFilterId) ||
      currentFilter.id.split(":").includes(filter.id) ||
      currentFilter.id.split(":").includes(durationFilterId);

    const matchesOdataId =
      currentFilter.odata.id === stripDurationId(effectiveFilterId);

    const matchesScope = currentFilter.odata.scope === filter.scope;

    const matchesCustom = !compare || compare(filter, currentFilter);

    if (containsId && matchesOdataId && matchesScope && matchesCustom) {
      const matchLength = countMatchingValues(
        currentFilter.odata.with ?? {},
        filter.with ?? {},
      );
      if (matchLength > maxMatchLength) {
        matchingFilter = currentFilter;
        maxMatchLength = matchLength;
      }
    }
  }

  return matchingFilter;
}

const countMatchingValues = (
  obj1: Record<PropertyKey, unknown>,
  obj2: Record<PropertyKey, unknown>,
): number => {
  let count = 0;
  for (const key in obj1) {
    if (
      key in obj1 &&
      key in obj2 &&
      obj1[key] === obj2[key] &&
      obj1[key] !== undefined &&
      obj1[key] !== null
    ) {
      count += 1;
    }
  }
  return count;
};

export function convertFiltersToFormFilters<
  F extends Filter,
  AF extends AvailableFilter,
>(
  currentFilters: F[],
  filters: AF[],
  compare?: (currentFilter: F, filter: AF) => boolean,
): (Omit<F, "operator"> & FormFilter)[] {
  return currentFilters.map((filter) => {
    const formField = findMatchingFilter(filter, filters, compare);
    const id = formField?.id ?? filter.id;
    const parse = formField?.parse ?? DEFAULT_PARSE;
    return { ...filter, ...parse(filter), id };
  });
}

export function convertFormFiltersToFilters(
  currentFilters: FormFilter[],
  filters: AvailableFilter[],
): Filter[] {
  return currentFilters.map((filter) => {
    const formField = filters.find((c) => c.id === filter.id);
    const cast = formField?.cast ?? getDefaultCast(formField?.type ?? "text");
    return cast({
      ...filter,
      ...formField?.odata,
      isArray: formField?.isArray ?? false,
    });
  });
}

type FilterFormProps = {
  scmCategory: SCMCategory;
  availableFilters: AvailableFilter[];
  defaultFilters: Filter[];
  onSubmit: (data: Filter[]) => void;
  onSave?: (data: Filter[]) => Promise<void>;
  onClear: () => void;
  onClose: () => void;
};

export interface FilterFormHandle {
  resetForm: (newFilters: Filter[]) => void;
}

export const FilterForm = forwardRef<FilterFormHandle, FilterFormProps>(
  (
    {
      scmCategory,
      availableFilters,
      defaultFilters,
      onSubmit,
      onSave,
      onClear,
      onClose,
    },
    ref,
  ) => {
    const form = useForm<FilterFormData>({
      defaultValues: {
        filters:
          defaultFilters.length > 0
            ? convertFiltersToFormFilters(defaultFilters, availableFilters)
            : DEFAULT_FILTERS,
      },
      resolver: zodResolver(filterFormSchema),
    });

    useImperativeHandle(ref, () => ({
      resetForm: (newFilters: Filter[]) => {
        form.reset({
          filters:
            newFilters.length > 0
              ? convertFiltersToFormFilters(newFilters, availableFilters)
              : DEFAULT_FILTERS,
        });
      },
    }));

    const { control, handleSubmit, trigger, getValues } = form;

    async function submit(data: FilterFormData) {
      onSubmit(convertFormFiltersToFilters(data.filters, availableFilters));
      onClose();
    }

    async function save() {
      const isValid = await trigger();
      if (isValid && onSave) {
        await onSave(
          convertFormFiltersToFilters(getValues().filters, availableFilters),
        );
      }
    }

    const { fields, append, remove } = useFieldArray({
      control,
      name: "filters",
    });

    return (
      <FormProvider {...form}>
        <ScrollAreaRoot>
          <ScrollAreaViewport className="max-h-[500px]">
            <form
              className="flex max-w-[90vw] flex-col justify-between gap-2 p-4"
              onSubmit={handleSubmit(submit)}
            >
              <FilterFormFields
                scmCategory={scmCategory}
                fields={fields}
                onRemove={remove}
                availableFilters={availableFilters}
              />
              <div className="flex gap-2">
                <FilterFormActions
                  canApply
                  onAdd={append}
                  onSave={save}
                  onClear={() => {
                    onClear();
                    onClose();
                  }}
                />
              </div>
            </form>
          </ScrollAreaViewport>
          <ScrollBar orientation="vertical" />
        </ScrollAreaRoot>
      </FormProvider>
    );
  },
);

FilterForm.displayName = "FilterForm";

const findColumnMetaFilter = (
  availableFilters: AvailableFilter[],
  id: string | undefined,
) => (id ? availableFilters.find((c) => c.id === id) : undefined);

interface FilterFormFieldsProps {
  fields: FormFilter[];
  scmCategory: SCMCategory;
  onRemove: (index: number) => void;
  availableFilters: AvailableFilter[];
  disabled?: boolean;
  readOnly?: boolean;
}

export const FilterFormFields = ({
  fields,
  ...props
}: FilterFormFieldsProps) => {
  return fields.map((field, index) => (
    <FilterFormField
      key={field.id}
      field={field}
      index={index}
      showConjunction
      {...props}
    />
  ));
};

interface FilterFormFieldProps extends Omit<FilterFormFieldsProps, "fields"> {
  field: FormFilter;
  index: number;
  scmCategory: SCMCategory;
  showConjunction?: boolean;
  onRemove: (index: number) => void;
  availableFilters: AvailableFilter[];
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
}

interface FilterValueInputProps {
  type: AvailableFilter["type"] | undefined;
  value: string | null;
  onChange: (value: string | null) => void;
  name?: string;
  operator: OdataOperator | "";
  options?: FilterOption[];
  searchableOptions?: boolean;
  onBlur?: () => void;
  readOnly?: boolean;
  disabled?: boolean;
}

export const FilterValueInput = ({
  type,
  value,
  operator,
  options,
  searchableOptions,
  onChange,
  name,
  onBlur,
  readOnly,
  disabled,
  "data-testid": dataTestId,
}: FilterValueInputProps & { "data-testid"?: string }) => {
  const showNullCheckbox = operator === "is" || operator === "is not";
  const isNull = value === null;

  if (showNullCheckbox) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1">{renderValueInput()}</div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="null-checkbox"
            checked={isNull}
            onCheckedChange={(checked) => {
              if (checked) {
                onChange(null);
              } else {
                onChange("");
              }
            }}
            disabled={disabled || readOnly}
          />
          <label htmlFor="null-checkbox" className="text-sm">
            NULL
          </label>
        </div>
      </div>
    );
  }

  return renderValueInput();

  function renderValueInput() {
    switch (type) {
      case "duration": {
        return (
          <DurationInput
            placeholder={value === null ? "NULL" : ""}
            value={value === null || value === "" ? undefined : Number(value)}
            defaultUnit="days"
            onChange={(val) => onChange(val === undefined ? "" : String(val))}
            allowedUnits={["days"]}
            readOnly={readOnly}
            disabled={disabled || isNull}
            className="flex-1"
          />
        );
      }
      case "date": {
        return (
          <DateInput
            value={value ?? ""}
            name={name ?? ""}
            onBlur={onBlur}
            onChange={onChange}
            readOnly={readOnly}
            disabled={disabled || isNull}
            wrapperClassName="flex-1"
            data-testid={dataTestId}
          />
        );
      }
      case "boolean": {
        return (
          <Select
            name={name}
            onBlur={onBlur}
            value={value ?? ""}
            onChange={(e) => {
              onChange(e.target.value ?? null);
            }}
            placeholder={isNull ? "NULL" : "Select a value"}
            readOnly={readOnly}
            disabled={disabled || isNull}
            className="flex-1"
          >
            <option disabled value="">
              {isNull ? "NULL" : "Select a value"}
            </option>
            <option value="true">True</option>
            <option value="false">False</option>
          </Select>
        );
      }
      case "text":
      case "number":
      case undefined: {
        const isExactMatchSearchableDropdown = Boolean(
          options &&
            searchableOptions &&
            (operator === "is" || operator === "is not"),
        );
        const isUnsearchableDropdown = Boolean(options && !searchableOptions);

        if (
          (isExactMatchSearchableDropdown || isUnsearchableDropdown) &&
          options
        ) {
          return (
            <FilterValueSelect
              type={type}
              value={value ?? ""}
              operator={operator}
              name={name}
              onBlur={onBlur}
              selection={[value ?? ""]}
              options={options}
              onChange={onChange}
              readOnly={readOnly}
              disabled={disabled || readOnly || isNull}
            />
          );
        }

        return (
          <Input
            name={name}
            onBlur={onBlur}
            value={value ?? ""}
            type={type}
            wrapperClassName="flex-1"
            onChange={(e) => {
              onChange(e.target.value);
            }}
            placeholder={isNull ? "NULL" : "Required"}
            className="flex-1 placeholder:text-secondary-foreground"
            required
            readOnly={readOnly}
            disabled={disabled || readOnly || isNull}
            data-testid={dataTestId}
          />
        );
      }
      default: {
        return assertNever(type);
      }
    }
  }
};

const FilterValueSelect = ({
  value,
  options,
  onChange,
  name,
  onBlur,
  readOnly,
  disabled,
  selection,
}: FilterValueInputProps & {
  options: FilterOption[];
  selection: string[];
}) => {
  return (
    <DropdownSelect
      intent="secondary"
      name={name}
      onBlur={onBlur}
      selection={selection}
      options={options}
      placeholder="Select a value"
      getLabel={(o) => o.label}
      getValue={(o) => String(o.value ?? "")}
      onChangeSelection={onChange}
      className="flex-1 font-normal placeholder:text-secondary-foreground"
      disabled={disabled || readOnly}
    >
      {options.find((o) => String(o.value ?? "") === (value ?? ""))?.label ??
        "Select a value"}
    </DropdownSelect>
  );
};

export const FilterFormField = ({
  field,
  index,
  scmCategory,
  availableFilters,
  showConjunction,
  onRemove,
  disabled,
  readOnly,
  className,
}: FilterFormFieldProps) => {
  const { control, register, setValue, watch } =
    useFormContext<FilterFieldValues>();

  const currentFilters = watch("filters");
  const {
    fields: orFields,
    append: appendOr,
    remove: removeOr,
  } = useFieldArray({
    control,
    name: `filters.${index}.ors`,
  });

  return (
    <div
      className={cn(
        "grid w-full grid-cols-1 items-start gap-2 md:grid-cols-5",
        className,
      )}
      key={field.id}
    >
      <div className="col-span-2 flex items-end gap-4">
        {showConjunction && index !== 0 && (
          <div className="mb-1.5 w-max rounded-md border border-stroke-line px-2 py-1 font-medium text-secondary-foreground text-xs">
            AND
          </div>
        )}
        <DropdownFormField
          {...register(`filters.${index}.id`, {
            onChange: (event) => {
              const filter = findColumnMetaFilter(
                availableFilters,
                currentFilters[index]?.id,
              );
              setValue(`filters.${index}.id`, event.target.value);
              setValue(`filters.${index}.scope`, filter?.odata?.scope);
              setValue(`filters.${index}.with`, { ...filter?.odata?.with });
              setValue(`filters.${index}.isArray`, filter?.isArray);
              setValue(`filters.${index}.quantifier`, "any");
              setValue(`filters.${index}.operator`, "");
              setValue(`filters.${index}.value`, "");
              setValue(`filters.${index}.ors`, []);
            },
          })}
          label="Filter Column"
          required
          readOnly={readOnly}
          disabled={disabled}
          options={getFilterOptions({ availableFilters })}
          className="w-full min-w-0"
          placeholder="Select a column"
          data-testid={`filter-column-${index}`}
        />
      </div>
      <DropdownFormField
        {...register(`filters.${index}.operator`, {
          onChange: () => {
            if (watch(`filters.${index}.value`) === null) {
              setValue(`filters.${index}.value`, "");
            }
          },
        })}
        label="Operator"
        required
        readOnly={readOnly}
        disabled={disabled || !currentFilters[index]?.id}
        options={getOperatorOptions(
          findColumnMetaFilter(availableFilters, currentFilters[index]?.id),
        )}
        className="col-span-1 w-full"
        placeholder="Select an operator"
        data-testid={`filter-operator-${index}`}
      />
      <Controller
        name={`filters.${index}.value`}
        control={control}
        disabled={disabled}
        render={({ field: valueField, fieldState: { error } }) => {
          const filter = findColumnMetaFilter(
            availableFilters,
            currentFilters[index]?.id,
          );
          const type = filter?.type;
          const options = filter?.options;
          const searchableOptions = filter?.searchableOptions;
          const operator = watch(`filters.${index}.operator`);

          const onChange = (val: string | null) => {
            setValue(`filters.${index}.value`, val);
          };

          return (
            <InputField
              label="Value"
              required
              error={error?.message ?? error?.root?.message}
              className="col-span-2 w-full"
            >
              <div className="flex items-center gap-2">
                <FilterValueInput
                  type={type}
                  value={valueField.value}
                  operator={operator}
                  options={options}
                  searchableOptions={searchableOptions}
                  onChange={onChange}
                  name={valueField.name}
                  onBlur={valueField.onBlur}
                  readOnly={readOnly}
                  disabled={disabled}
                  data-testid={`filter-value-${index}`}
                />
                <Button
                  intent="secondary"
                  type="button"
                  size="small"
                  disabled={disabled || !currentFilters[index]?.id}
                  onClick={() =>
                    appendOr({
                      operator: "is",
                      value: "",
                    })
                  }
                  className="whitespace-nowrap"
                >
                  + OR
                </Button>

                <IconButton
                  icon={
                    <TrashcanIcon className="hover:fill-primary-foreground" />
                  }
                  onClick={() => onRemove(index)}
                  disabled={disabled}
                  className="border border-stroke-line p-2.5 hover:bg-tertiary-foreground"
                />
              </div>
            </InputField>
          );
        }}
      />

      {orFields.length > 0 && (
        <>
          {orFields.map((orField, orIndex) => (
            <Fragment key={orField.id}>
              <div className="col-span-2 flex items-center justify-end pt-7">
                <div className="w-max rounded-md border border-stroke-line px-2 py-1 font-medium text-secondary-foreground text-xs">
                  OR
                </div>
              </div>
              <DropdownFormField
                {...register(`filters.${index}.ors.${orIndex}.operator`, {
                  onChange: () => {
                    if (
                      watch(`filters.${index}.ors.${orIndex}.value`) === null
                    ) {
                      setValue(`filters.${index}.ors.${orIndex}.value`, "");
                    }
                  },
                })}
                label="Operator"
                required
                readOnly={readOnly}
                disabled={disabled}
                options={getOperatorOptions(
                  findColumnMetaFilter(
                    availableFilters,
                    currentFilters[index]?.id,
                  ),
                )}
                className="w-full"
                placeholder="Select an operator"
              />
              <Controller
                name={`filters.${index}.ors.${orIndex}.value`}
                control={control}
                disabled={disabled}
                render={({ field: orValueField, fieldState: { error } }) => {
                  const filter = findColumnMetaFilter(
                    availableFilters,
                    currentFilters[index]?.id,
                  );
                  const type = filter?.type;
                  const options = filter?.options;
                  const searchableOptions = filter?.searchableOptions;
                  const operator = watch(
                    `filters.${index}.ors.${orIndex}.operator`,
                  );

                  const onChange = (val: string | null) => {
                    setValue(`filters.${index}.ors.${orIndex}.value`, val);
                  };

                  return (
                    <InputField
                      label="Value"
                      required
                      error={error?.message ?? error?.root?.message}
                      className="col-span-2 w-full"
                    >
                      <div className="flex items-center gap-2">
                        <FilterValueInput
                          type={type}
                          value={orValueField.value}
                          operator={operator}
                          options={options}
                          searchableOptions={searchableOptions}
                          onChange={onChange}
                          name={orValueField.name}
                          onBlur={orValueField.onBlur}
                          readOnly={readOnly}
                          disabled={disabled}
                        />
                        <IconButton
                          icon={<TrashcanIcon />}
                          onClick={() => removeOr(orIndex)}
                          disabled={disabled}
                          className="border border-stroke-line p-2"
                        />
                      </div>
                    </InputField>
                  );
                }}
              />
            </Fragment>
          ))}
        </>
      )}
    </div>
  );
};

export const getFilterOptions = ({
  availableFilters,
}: {
  availableFilters: AvailableFilter[];
}): SelectOption<string>[] => {
  const groupedFilterFieldsEntries = Object.entries(
    groupBy(availableFilters, (filter) => filter.group ?? ""),
  ).sort(priorityFilterSort);

  const groupedFilterFields = Object.fromEntries(groupedFilterFieldsEntries);

  const options = Object.entries(groupedFilterFields).flatMap(
    ([label, filterGroup]): SelectOption<string>[] => {
      const items = filterGroup.map((filterField) => ({
        label: filterField.label,
        group: label,
        value: filterField.id,
      }));
      return items;
    },
  );

  return options;
};

const priorityFilterSort = (
  a: [string, AvailableFilter[]],
  b: [string, AvailableFilter[]],
) => {
  if (a[0] === "" && b[0] !== "") return -1;
  if (a[0] !== "" && b[0] === "") return 1;
  if (a[0] === "Control Failures" && b[0] !== "Control Failures") return -1;
  if (a[0] !== "Control Failures" && b[0] === "Control Failures") return 1;
  return a[0].localeCompare(b[0]);
};

const getOperatorOptions = (
  filter: AvailableFilter | undefined,
): SelectOption<string>[] => {
  const options: SelectOption<string>[] =
    filter?.operators?.map((operator) => ({
      label: operator,
      value: operator,
    })) ?? [];

  return options;
};

export const FilterFormActions = ({
  canApply = false,
  onAdd,
  onClear,
  disabled = false,
  onSave,
}: {
  canApply?: boolean;
  onAdd: (filter: FormFilter) => void;
  onClear: () => void;
  disabled?: boolean;
  onSave?: () => Promise<void>;
}) => (
  <div className="mt-1 flex w-full justify-between gap-2">
    <div className="flex gap-2">
      <div className="flex gap-2">
        <Button
          icon={<PlusIcon className="fill-secondary-foreground" />}
          intent="secondary"
          type="button"
          disabled={disabled}
          onClick={() =>
            onAdd({
              id: "",
              operator: "",
              value: "",
              ors: [],
            })
          }
        >
          Add filter
        </Button>
        {canApply && (
          <Button intent="primary" disabled={disabled}>
            Apply
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          intent="text-link"
          type="button"
          disabled={disabled}
          onClick={onClear}
        >
          Clear all
        </Button>
      </div>
    </div>
    {onSave && <SaveChangesToView onSave={onSave} />}
  </div>
);

function SaveChangesToView({ onSave }: { onSave: () => Promise<void> }) {
  const save = useMutation({
    mutationFn: onSave,
  });
  return (
    <Button
      onClick={() => save.mutate()}
      type="button"
      intent="secondary"
      disabled={save.isPending}
      loading={save.isPending}
    >
      Save Changes to View
    </Button>
  );
}

export function getFilterCount(filters: Filter[]) {
  return (
    filters.length +
    filters.reduce((acc, filter) => acc + (filter.ors?.length || 0), 0)
  );
}
